/**
 * « Le libre-service est-il ouvert ici ? » — et ce qu'on affiche quand non.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI CET ÉCRAN EXISTE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un formulaire d'inscription ouvert sur un environnement qui ne sait pas
 * servir `<slug>.<domaine>` n'est pas seulement inutile : il est nuisible. Le
 * prospect remplit trois champs, reçoit un courriel qui lui annonce fièrement
 * son adresse — et cette adresse ne résout pas. C'est le tout premier message
 * qu'il reçoit de nous, et il est faux.
 *
 * Constaté le 30/08/2026 : c'était l'état de la production, dont le DNS
 * wildcard, le certificat et la règle de routage multi-hôte n'existaient pas,
 * pendant que le formulaire, lui, invitait.
 *
 * Le serveur décide (`INSCRIPTION_PUBLIQUE`, fermé par défaut en production) ;
 * cet écran ne fait que le dire, et proposer la seule voie qui reste.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, Outlet } from 'react-router'

import { EnteteVitrine } from '../vitrine/EnteteVitrine'
import { PiedVitrine } from '../vitrine/PiedVitrine'
import { SectionContact } from '../vitrine/SectionContact'
import { Conteneur } from '../vitrine/elements'
import { Spinner } from '../../ui/Spinner'
import { lireInscriptionOuverte } from './api'

export function useInscriptionOuverte() {
  return useQuery({
    queryKey: ['inscription', 'ouverte'],
    queryFn: () => lireInscriptionOuverte(),
    /* La réponse ne change qu'au redéploiement : inutile de la redemander. */
    staleTime: 10 * 60_000,
    retry: false,
  })
}

/**
 * Ce que voit quelqu'un qui arrive sur `/inscription` quand elle est fermée.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ C'ÉTAIT UN CUL-DE-SAC, ET IL ÉTAIT AU BOUT DU SEUL BOUTON QUI COMPTE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La vitrine invite CINQ fois à « Essayer trente jours » — dans l'en-tête, dans
 * le hero, sous les six étapes, au tarif, en clôture — et la documentation
 * publique promet d'« ouvrir une instance à votre nom, sans parler à
 * personne ». En production, où le libre-service est fermé par défaut tant que
 * le DNS joker n'existe pas, ces six invitations arrivaient toutes ici : une
 * page sans en-tête, sans logo, sans le moindre lien de retour, portant une
 * adresse électronique en texte et rien d'autre.
 *
 * Le message était juste, et c'est bien le problème : il était juste et il ne
 * servait à rien. Le prospect le plus motivé de la page — celui qui a cliqué —
 * se retrouvait dans une impasse, sans même le moyen de revenir à ce qu'il
 * lisait. Rien ne ramassait sa demande.
 *
 * Ce que la page fait maintenant, dans cet ordre :
 *
 *  1. **elle garde la page autour d'elle** — l'en-tête et le pied de la
 *     vitrine reviennent : on peut repartir vers le tarif ou la documentation ;
 *  2. **elle dit ce qui se passe, et en combien de temps** — « à la main »
 *     n'est pas une excuse, c'est un délai ;
 *  3. ★ **elle porte le formulaire de contact**, celui-là même que la vitrine
 *     présente en section 16. Une demande d'essai devient une demande d'accès,
 *     et elle arrive quelque part au lieu de dépendre du fait que quelqu'un
 *     ouvre son client de messagerie.
 *
 * ⚠️ Ce n'est PAS la correction du défaut de fond, seulement son rattrapage.
 * Le défaut de fond est que la recette — l'environnement fait pour montrer
 * l'essai — se déclarait « production » à cause de `NODE_ENV` et fermait donc
 * le libre-service. Il est corrigé dans `core/config.ts` (`APP_ENV`). Cette
 * page reste ce qu'on voit là où le libre-service est légitimement fermé.
 */
export function EcranInscriptionFermee() {
  return (
    <div className="min-h-dvh bg-slate-50 text-slate-700">
      <EnteteVitrine />

      <main>
        <section className="affiche-poursuite relative isolate overflow-hidden bg-nuit-950 text-slate-100">
          <div
            aria-hidden="true"
            className="affiche-trame pointer-events-none absolute inset-0 -z-10 text-white/[0.08]"
          />
          <Conteneur className="relative py-14 sm:py-20">
            <p className="text-[0.8125rem] font-semibold tracking-[0.2em] text-rideau-300 uppercase">
              Ouverture d’une billetterie
            </p>
            <h1 className="mt-5 max-w-[20ch] text-[clamp(1.875rem,5vw,3rem)] leading-[1.02] font-semibold tracking-[-0.03em] text-white">
              Ici, nous ouvrons votre instance à la main.
            </h1>
            <p className="mt-6 max-w-[62ch] text-[1.0625rem] leading-relaxed text-slate-300">
              Ce n’est pas une panne, et vous n’avez rien fait de travers&nbsp;: la création en
              libre-service n’est pas ouverte sur cette adresse. Dites-nous le nom de votre salle
              ci-dessous — nous montons votre instance, avec le jeu de démonstration si vous le
              souhaitez, et nous vous envoyons vos identifiants.
            </p>
            <p className="mt-4 max-w-[62ch] text-[0.9375rem] text-slate-400">
              Les trente jours d’essai sont les mêmes, aux mêmes conditions&nbsp;: sans engagement,
              sans carte bancaire, et l’essai ne devient un abonnement que si vous le décidez. Vous
              pouvez aussi écrire directement à{' '}
              <a href="mailto:info@ilygo.ch" className="text-white underline underline-offset-2">
                info@ilygo.ch
              </a>
              .
            </p>

            <p className="mt-8 text-[0.9375rem] text-slate-400">
              En attendant, la{' '}
              <Link to="/aide" className="text-white underline underline-offset-2">
                documentation complète
              </Link>{' '}
              est publique&nbsp;: trente-neuf rubriques, aucun compte à créer pour les lire.
            </p>
          </Conteneur>
        </section>

        {/* ★ Le formulaire de la vitrine, tel quel : une seule route de contact,
            un seul gabarit de courriel, un seul endroit à maintenir. */}
        <SectionContact />
      </main>

      <PiedVitrine />
    </div>
  )
}

/**
 * La garde de route : elle décide, les formulaires ignorent la question.
 *
 * L'attente dure une demi-seconde, mais afficher le formulaire puis le retirer
 * serait pire que d'attendre — quelqu'un aurait commencé à taper.
 *
 * En cas d'échec de la requête, on laisse passer : un environnement dont on
 * n'arrive pas à lire l'état vaut mieux ouvert que muet, et le serveur refuse
 * de toute façon le dépôt s'il est fermé (403 `INSCRIPTION_FERMEE`). La garde
 * est là pour éviter de faire perdre son temps à quelqu'un, pas pour être la
 * serrure — la serrure est côté serveur.
 */
export function GardeInscriptionOuverte() {
  const ouverture = useInscriptionOuverte()

  if (ouverture.isPending) {
    return (
      <main className="mx-auto flex max-w-2xl justify-center px-4 py-20">
        <Spinner />
      </main>
    )
  }
  if (ouverture.data?.ouverte === false) return <EcranInscriptionFermee />
  return <Outlet />
}
